// TenantGate — sits between auth and the dashboard shell. After a first login
// with no tenant, it shows a minimal onboarding form that calls tenantSignup;
// once users/{uid} exists, it renders the app (PRD §10 P1).
import { useEffect, useRef, useState, type FormEvent, type ReactElement } from 'react';
import { useI18n } from '../lib/i18n';
import { provisionTenant, useTenant } from '../lib/tenant';

function Spinner() {
  return (
    <div className="min-h-screen grid place-items-center bg-surface">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  );
}

function TenantOnboarding() {
  const { t } = useI18n();
  // If Signup's provisioning failed we land here; reuse the name they typed and
  // say why they're seeing this screen instead of a blank "create workspace".
  const pendingName = sessionStorage.getItem('watag.pendingWorkspaceName');
  const [businessName, setBusinessName] = useState(pendingName ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(pendingName ? t('signup.provisionFailed') : null);
  useEffect(() => { if (pendingName) sessionStorage.removeItem('watag.pendingWorkspaceName'); }, [pendingName]);
  const watchdog = useRef<ReturnType<typeof setTimeout>>();

  // Clear the watchdog if the gate advances (this component unmounts) first.
  useEffect(() => () => clearTimeout(watchdog.current), []);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await provisionTenant(businessName.trim());
      // On success the users/{uid} snapshot should flip the gate to the app.
      // Guard against a stuck listener (e.g. a rules regression) so we never
      // spin on "Creating…" forever — surface a retry after 15s instead.
      watchdog.current = setTimeout(() => {
        setError(t('onboarding.stuck'));
        setBusy(false);
      }, 15_000);
    } catch {
      setError(t('onboarding.error'));
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface p-4">
      <div className="w-full max-w-md rounded-card border border-slate-200 bg-white p-7 shadow-sm">
        <h1 className="text-xl font-extrabold text-ink">{t('onboarding.title')}</h1>
        <p className="mb-6 mt-1 text-sm text-ink-3">{t('onboarding.subtitle')}</p>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label htmlFor="businessName" className="mb-1 block text-sm font-semibold text-ink-2">
              {t('onboarding.business')}
            </label>
            <input
              id="businessName"
              type="text"
              required
              autoFocus
              maxLength={120}
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary-50"
            />
          </div>
          {error && (
            <p role="alert" className="text-sm font-semibold text-red-600">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={busy || !businessName.trim()}
            className="w-full rounded-lg bg-primary py-2.5 font-bold text-white hover:bg-primary-600 disabled:opacity-60"
          >
            {busy ? t('onboarding.loading') : t('onboarding.submit')}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function TenantGate({ children }: { children: ReactElement }) {
  const { loading, tenantId } = useTenant();
  if (loading) return <Spinner />;
  if (!tenantId) return <TenantOnboarding />;
  return children;
}
